import React, { useRef, useState, useEffect } from "react";

/**
 * Finger/mouse signature pad. Returns dataURL via onSave.
 */
export default function SignaturePad({ onSave, onCancel, initial = null }) {
  const canvasRef = useRef(null);
  const drawing = useRef(false);
  const last = useRef({ x: 0, y: 0 });
  const [empty, setEmpty] = useState(!initial);

  useEffect(() => {
    const c = canvasRef.current;
    if (!c) return;
    // hi-dpi
    const rect = c.getBoundingClientRect();
    const dpr = window.devicePixelRatio || 1;
    c.width = rect.width * dpr;
    c.height = rect.height * dpr;
    const ctx = c.getContext("2d");
    ctx.scale(dpr, dpr);
    ctx.lineWidth = 2.2;
    ctx.lineCap = "round";
    ctx.lineJoin = "round";
    ctx.strokeStyle = "#ffffff";
    ctx.fillStyle = "#0e0e12";
    ctx.fillRect(0, 0, rect.width, rect.height);
    if (initial) {
      const img = new Image();
      img.onload = () => ctx.drawImage(img, 0, 0, rect.width, rect.height);
      img.src = initial;
    }
  }, [initial]);

  const pos = (e) => {
    const rect = canvasRef.current.getBoundingClientRect();
    const t = e.touches?.[0];
    const x = (t ? t.clientX : e.clientX) - rect.left;
    const y = (t ? t.clientY : e.clientY) - rect.top;
    return { x, y };
  };

  const start = (e) => {
    e.preventDefault();
    drawing.current = true;
    last.current = pos(e);
  };
  const move = (e) => {
    if (!drawing.current) return;
    e.preventDefault();
    const p = pos(e);
    const ctx = canvasRef.current.getContext("2d");
    ctx.beginPath();
    ctx.moveTo(last.current.x, last.current.y);
    ctx.lineTo(p.x, p.y);
    ctx.stroke();
    last.current = p;
    setEmpty(false);
  };
  const end = () => { drawing.current = false; };

  const clear = () => {
    const c = canvasRef.current;
    const ctx = c.getContext("2d");
    const rect = c.getBoundingClientRect();
    ctx.fillStyle = "#0e0e12";
    ctx.fillRect(0, 0, rect.width, rect.height);
    setEmpty(true);
  };

  const save = () => {
    if (empty) return;
    const dataUrl = canvasRef.current.toDataURL("image/png");
    onSave(dataUrl);
  };

  return (
    <div data-testid="signature-pad">
      <div className="relative">
        <canvas
          ref={canvasRef}
          className="w-full h-48 md:h-64 rounded-xl border border-white/15 touch-none"
          onMouseDown={start} onMouseMove={move} onMouseUp={end} onMouseLeave={end}
          onTouchStart={start} onTouchMove={move} onTouchEnd={end}
          data-testid="signature-canvas"
        />
        <div className="absolute bottom-2 left-3 font-mono-alt text-[9px] text-white/25 pointer-events-none">
          Sign with finger or stylus
        </div>
      </div>
      <div className="mt-3 flex items-center gap-2">
        <button type="button" onClick={clear} data-testid="signature-clear"
          className="px-4 py-2 rounded-lg border border-white/15 text-white/60 text-sm hover:text-white hover:bg-white/5">Clear</button>
        <button type="button" onClick={save} disabled={empty} data-testid="signature-save"
          className="ml-auto px-6 py-2 rounded-lg bg-[#1F5EFF] hover:bg-white hover:text-black text-white text-sm font-medium disabled:opacity-40">
          Confirm signature
        </button>
        {onCancel && (
          <button type="button" onClick={onCancel} data-testid="signature-cancel"
            className="px-4 py-2 text-white/50 hover:text-white text-sm">Cancel</button>
        )}
      </div>
    </div>
  );
}
